import Joi from "joi-browser";
import { useState, useEffect } from "react";
import { useForm } from "V2.0/HomePages/Assistants/AssistantTools/CustomAPIs/components/Form/useForm";
import Form from "V2.0/HomePages/Assistants/AssistantTools/CustomAPIs/components/Form";
import { Button } from "V2.0/common/button";
import { MonitoringSetting } from "./MonitoringSettings/MonitoringSettings";
import {
  PencilIcon,
  PlusIcon,
  TrashIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { Dialog, DialogTitle, DialogActions } from "V2.0/common/dialog";
import { Badge } from "V2.0/common/badge";
import { NODE_STATUS_COLORS } from "../index2";
import { ErrorMessage } from "V2.0/common/fieldset";
import { useAssistants } from "V2.0/HomePages/Assistants";
import { Tooltip } from "antd";
import { NodeStatus } from "../models/nodeStatus";

const template = [
  {
    name: "assistant",
    label: "Assistant",
    type: "select",
    options: [
      { id: 0, label: "(None selected)", value: "" },
      { id: 1, label: "Sms", value: "sms" },
      { id: 2, label: "WhatsApp", value: "whatsapp" },
    ],
  },
  // {
  //   name: "fromNumber",
  //   label: "From Number",
  //   type: "text",
  // },
];

const schema = {
  name: Joi.string().required(),
  description: Joi.string().allow(null, ""),
  assistant: Joi.string().required().label("Assistant"),
  monitoringSettings: Joi.array()
    .items(
      Joi.object({
        triggerDescription: Joi.string().required(),
        statusToSet: Joi.string().required(),
      }).required()
    )
    .min(1)
    .label("Condition"),
  // fromNumber: Joi.string()
  //   .regex(/^\+?[0-9]+$/)
  //   .allow(null, "")
  //   .label("From Number"),
};

function AssistantNodeSettings(props) {
  const {
    node,
    setNodes,
    closePanel,
    projectId,
    notifyChange,
    options,
    pipelineType,
  } = props;
  const [assistantNodeSettings, setAssistantNodeSettings] = useState({
    ...node?.data?.settings,
  });
  const [state] = useState({
    name: node?.data?.settings?.name || "",
    description: node?.data?.settings?.description || "",
    assistant: node?.data?.settings?.assistant || "",
  });
  const [loading, setLoading] = useState(false);
  const [showMonitoringSettings, setShowMonitoringSettings] = useState({
    isOpen: false,
    mode: null,
    item: null,
    index: null,
  });
  const { data, error, validate, handleChange, setError } = useForm(
    state,
    schema
  );
  const { assistants, fetchAssistants } = useAssistants(projectId);

  useEffect(() => {
    if (projectId) {
      fetchAssistants();
    }
  }, [projectId]);

  function addMonitoringSetting(monitoringSetting) {
    if (error.monitoringSettings) {
      setError((prevError) => {
        const { monitoringSettings, ...restError } = prevError;
        return restError;
      });
    }
    setAssistantNodeSettings((prevSettings) => {
      return {
        ...prevSettings,
        monitoringSettings: [
          ...prevSettings.monitoringSettings,
          monitoringSetting,
        ],
      };
    });
  }

  function updateMonitoringSetting(monitoringSetting, index) {
    setAssistantNodeSettings((prevSetting) => {
      return {
        ...prevSetting,
        monitoringSettings: prevSetting.monitoringSettings.toSpliced(
          index,
          1,
          monitoringSetting
        ),
      };
    });
  }

  function deleteMonitoringSetting(index) {
    setAssistantNodeSettings((prevSetting) => {
      return {
        ...prevSetting,
        monitoringSettings: prevSetting.monitoringSettings.toSpliced(index, 1),
      };
    });
  }

  function handleSave() {
    const payload = {
      ...assistantNodeSettings,
      ...data,
    };
    const errors = validate(payload, schema);

    if (errors) {
      setError(errors);
      return;
    }

    setNodes((nodes) => {
      const updatedNodes = nodes.map((_node) => {
        if (_node.id === node.id) {
          const { selected, ...rest } = _node;
          return {
            ...rest,
            selected: false,

            data: {
              ...node.data,
              settings: { ...node.data.settings, ...payload },
            },
          };
        } else {
          return _node;
        }
      });
      return updatedNodes;
    });
    closePanel();
    notifyChange();
  }

  const template = [
    {
      name: "name",
      label: "Name",
      type: "text",
    },
    {
      name: "description",
      label: "Description",
      type: "textArea",
      style: { height: "70px", fontSize: "13px" },
    },
    {
      name: "assistant",
      label: "Assistant",
      type: "select",
      options: [
        { id: 0, label: "(None selected)", value: "" },
        ...assistants
          ?.filter((assistant) => assistant.assistantType === pipelineType)
          .map((assistant, idx) => {
            return { id: idx + 1, label: assistant.name, value: assistant._id };
          }),
      ],
    },
    // {
    //   name: "fromNumber",
    //   label: "From Number",
    //   type: "text",
    // },
  ];

  useEffect(() => {
    if (options?.triggerSave) {
      handleSave();
    }
  }, [options]);

  return (
    <div className="flex flex-col gap-y-4">
      <Form
        data={data}
        handleChange={handleChange}
        error={error}
        template={template}
      />
      <div className="flex items-center justify-between border-b pb-4">
        <Tooltip title="Let the assistant know when to handover to the next step">
          <p className="text-sm font-semibold flex gap-x-2 items-center">
            Next Step Condition <InformationCircleIcon className="size-4" />
          </p>
        </Tooltip>
        {assistantNodeSettings?.monitoringSettings?.length === 0 && (
          <div>
            <Button
              outline
              onClick={() =>
                setShowMonitoringSettings({
                  isOpen: true,
                  mode: "add",
                  item: {
                    triggerDescription: "",
                    statusToSet: NodeStatus.Completed,
                  },
                  index: null,
                })
              }
            >
              <PlusIcon /> condition
            </Button>
          </div>
        )}
      </div>
      {assistantNodeSettings.monitoringSettings.length > 0 && (
        <div>
          {assistantNodeSettings.monitoringSettings.map(
            (monitoringSetting, index) => {
              return (
                <div className="w-full flex justify-between gap-x-2 mb-4">
                  <p>{monitoringSetting.triggerDescription}</p>
                  {/* <div>
                    <Badge
                      color={NODE_STATUS_COLORS[monitoringSetting.statusToSet]}
                    >
                      {monitoringSetting.statusToSet}
                    </Badge>
                  </div> */}
                  <div className="flex-none">
                    <Button
                      className="mr-2"
                      outline
                      onClick={() =>
                        setShowMonitoringSettings({
                          isOpen: true,
                          mode: "edit",
                          item: monitoringSetting,
                          index,
                        })
                      }
                    >
                      <PencilIcon />
                    </Button>
                    <Button
                      outline
                      onClick={() =>
                        setShowMonitoringSettings({
                          isOpen: true,
                          mode: "delete",
                          item: monitoringSetting,
                          index,
                        })
                      }
                    >
                      <TrashIcon />
                    </Button>
                  </div>
                </div>
              );
            }
          )}
        </div>
      )}
      {error.monitoringSettings && (
        <ErrorMessage>Add atleast 1 condition</ErrorMessage>
      )}
      <div className="flex self-end">
        <Button onClick={handleSave}>save</Button>
      </div>
      {showMonitoringSettings.isOpen &&
      showMonitoringSettings.mode != "delete" ? (
        <MonitoringSetting
          open={showMonitoringSettings.isOpen}
          onClose={() =>
            setShowMonitoringSettings({
              isOpen: false,
              mode: null,
              item: null,
              index: null,
            })
          }
          onSave={
            showMonitoringSettings.mode === "add"
              ? addMonitoringSetting
              : updateMonitoringSetting
          }
          monitoringSetting={showMonitoringSettings.item}
        />
      ) : (
        <Dialog
          open={showMonitoringSettings.isOpen}
          onClose={() =>
            setShowMonitoringSettings({
              isOpen: false,
              mode: null,
              item: null,
              index: null,
            })
          }
        >
          <DialogTitle>Delete Condition?</DialogTitle>
          <DialogActions>
            <Button
              outline
              onClick={() =>
                setShowMonitoringSettings({
                  isOpen: false,
                  mode: null,
                  item: null,
                  index: null,
                })
              }
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                deleteMonitoringSetting(showMonitoringSettings.index);
                setShowMonitoringSettings({
                  isOpen: false,
                  mode: null,
                  item: null,
                  index: null,
                });
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {}
    </div>
  );
}

export { AssistantNodeSettings };
